import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {addOrReplaceAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import {SaveToResultsStepDataName} from 'analysis-tool/AnalysisStepDataNames';
import {DataFrameColumnMappingTable} from 'analysis-tool/data-frame-creation/column-mapping/DataFrameColumnMappingTable';
import {buildPreviewOverRowLimitMessage} from 'analysis-tool/data-frame-creation/DataFrameCreationMessages';
import useDataMappingState from 'analysis-tool/structured-data-mapping/useDataMappingState';
import validateSchemaMapping, {
  validateColumnMapping,
} from 'analysis-tool/structured-data-mapping/validateDataMapping';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import StepConfigurationViewTemplate from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {trackEvent} from 'analytics/Analytics';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {OBJECTS_CREATABLE_PERMISSION} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {ActionMethod, ActionOrigin, ANALYSIS_TOOL_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {DataFrame, DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {
  GetSubsetDataFrame,
  GetSubsetDataFrameVariables,
} from 'data-frames/graphql/__generated/GetSubsetDataFrame';
import {DEFAULT_DATA_FRAME_ROW_LIMIT, GET_SUBSET_DATA_FRAME_QUERY} from 'data-frames/graphql/GetDataFrame';
import getUserColumns from 'data-frames/util/getUserColumns';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {SAVE_TO_RESULTS_STARTED_EVENT} from 'performance/processAnalysesEvents';
import QueryingResultSchemaSelector from 'results/ResultSchemaSelector';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import Stepper, {StepperStep} from 'ui-library/stepper/Stepper';

import ColumnMappingTable from './column-mapping-table/ColumnMappingTable';
import {updateMappings, updateResultSchema} from './DataMappingStateReducers';
import {DataMappingState, TargetSpecificMappingState} from './DataMappingTypes';
import SaveToResultsStepOutputPreview from './SaveToResultsStepOutputPreview';
import {
  COLUMN_MAPPING_CONFIG_BY_TARGET,
  REQUIRED_COLUMN_DEFS_BY_TARGET,
  SaveToResultsTarget,
} from './targetSpecificColumns';

const isFinalizingGlobalState = makeUseGlobalClientState<boolean>(false);

function trackAddSaveToResultsStepEvent(
  dataMappingState: DataMappingState,
  targetSpecificMappingState: TargetSpecificMappingState | null,
  dataFrame: DataFrame,
  dataFrameId: string
): void {
  const numDataFrameColumns = getUserColumns(dataFrame).length;
  const numResultSchemaFields = dataMappingState.resultSchema!.fields.length;
  const numMappings = dataMappingState.mappings.length;
  const numTargetSpecificMappings = (targetSpecificMappingState?.columnMappings ?? []).length;

  trackEvent(ANALYSIS_TOOL_ANALYTICS_ACTIONS.RESULTS_COLUMNS_MAPPED, {
    action_method: ActionMethod.BUTTON_CLICK,
    action_origin: ActionOrigin.ANALYSIS_TOOL_SAVE_TO_RESULTS_CONFIGURATION_VIEW,
    data_frame_id: dataFrameId,
    result_schema_id: dataMappingState.resultSchema!.id,
    num_result_columns: numResultSchemaFields,
    num_data_frame_columns: numDataFrameColumns,
    num_mappings: numMappings,
    target: targetSpecificMappingState?.target,
    target_version: targetSpecificMappingState?.targetVersion,
    num_target_specific_mappings: numTargetSpecificMappings,
  });
}

export interface InternalSaveToResultsStepConfigurationViewProps {
  clientStateId: string;
  inputDataFrame: DataFrame;
  dataMappingState: DataMappingState;
  setDataMappingState: (newDataMappingState: DataMappingState) => void;
  targetSpecificMappingState: TargetSpecificMappingState | null;
  setTargetSpecificMappingState: (newTargetSpecificMappingState: TargetSpecificMappingState | null) => void;
  addSaveToResultsStep: () => Promise<void>;
  onCancel: () => void;
}

export function InternalSaveToResultsStepConfigurationView({
  clientStateId,
  inputDataFrame,
  dataMappingState,
  setDataMappingState,
  targetSpecificMappingState,
  setTargetSpecificMappingState,
  addSaveToResultsStep,
  onCancel,
}: InternalSaveToResultsStepConfigurationViewProps): JSX.Element {
  const [isFinalizing, setIsFinalizing] = isFinalizingGlobalState.useGlobalClientState(clientStateId);

  const onFinalize = async (): Promise<void> => {
    setIsFinalizing(true);
    await addSaveToResultsStep();
    setIsFinalizing(false);
  };

  const targetColumnDefs =
    targetSpecificMappingState == null
      ? []
      : REQUIRED_COLUMN_DEFS_BY_TARGET[targetSpecificMappingState.target];

  const validationErrors = validateSchemaMapping(dataMappingState);
  const targetColumnValidationErrors =
    targetSpecificMappingState == null
      ? []
      : validateColumnMapping(
          targetSpecificMappingState.columnMappings,
          targetColumnDefs,
          inputDataFrame.columns,
          {
            outputColumnConfig: COLUMN_MAPPING_CONFIG_BY_TARGET[targetSpecificMappingState.target],
          }
        );

  const errorMessage =
    inputDataFrame.numRows && inputDataFrame.rows.length < inputDataFrame.numRows
      ? buildPreviewOverRowLimitMessage(DEFAULT_DATA_FRAME_ROW_LIMIT)
      : null;

  const setSelectedColumnsHandler = (selectedColumnDefs: Array<DataFrameColumnDef | null>): void => {
    if (targetSpecificMappingState == null) {
      return;
    }
    const targetColumnMappings = [];
    for (let i = 0; i < selectedColumnDefs.length; i++) {
      const targetColumnDef = targetColumnDefs[i];
      const selectedColumnDef = selectedColumnDefs[i];
      if (selectedColumnDef == null) {
        continue;
      }
      targetColumnMappings.push({
        inputColumnSystemName: selectedColumnDef.systemName,
        outputColumnSystemName: targetColumnDef.systemName,
      });
    }
    setTargetSpecificMappingState({
      ...targetSpecificMappingState,
      columnMappings: targetColumnMappings,
    });
  };

  const dataFrameColumnSystemNameToColumnDefMap: Record<string, DataFrameColumnDef> = {};
  for (const colDef of inputDataFrame.columns) {
    dataFrameColumnSystemNameToColumnDefMap[colDef.systemName] = colDef;
  }

  const targetColumnSystemNameToDataFrameColumnSystemNameMap: Record<string, string> = {};
  for (const columnMapping of targetSpecificMappingState?.columnMappings ?? []) {
    targetColumnSystemNameToDataFrameColumnSystemNameMap[columnMapping.outputColumnSystemName] =
      columnMapping.inputColumnSystemName;
  }

  const selectedColumns: Array<DataFrameColumnDef | null> = targetColumnDefs.map((targetColumnDef) => {
    if (!targetColumnSystemNameToDataFrameColumnSystemNameMap.hasOwnProperty(targetColumnDef.systemName)) {
      return null;
    }
    const dataFrameColumnSystemName =
      targetColumnSystemNameToDataFrameColumnSystemNameMap[targetColumnDef.systemName];

    const dataFrameColumnDef = dataFrameColumnSystemNameToColumnDefMap[dataFrameColumnSystemName];

    return dataFrameColumnDef;
  });

  return (
    <StepConfigurationViewTemplate
      clientStateId={clientStateId}
      dataFrames={[inputDataFrame]}
      renderAdditionalPreviewContent={() => (
        <SaveToResultsStepOutputPreview
          clientStateId={`${clientStateId}_preview`}
          inputDataFrame={inputDataFrame}
          dataMappingState={dataMappingState}
          targetSpecificMappingState={targetSpecificMappingState}
        />
      )}
      disableFinalizeButton={
        dataMappingState.resultSchema == null ||
        dataMappingState.mappings.length === 0 ||
        validationErrors.length > 0 ||
        targetColumnValidationErrors.filter((e) => e.level === 'ERROR').length > 0
      }
      finalizeButtonText="Save to Results"
      finalizingLoadingMessage="Creating Results..."
      messages={errorMessage != null ? [errorMessage] : []}
      onCancel={onCancel}
      onFinalize={onFinalize}
      title="Save to Results"
      isFinalizing={isFinalizing}
    >
      <Stepper>
        <StepperStep title="Select Result schema" id="schema">
          <QueryingResultSchemaSelector
            selectedSchema={dataMappingState.resultSchema}
            onChange={(newResultSchema) => {
              setDataMappingState(
                updateResultSchema(dataMappingState, newResultSchema, inputDataFrame.columns)
              );
            }}
            filter={(resultSchema) => !isArchived(resultSchema)}
            requiredPermissions={[OBJECTS_CREATABLE_PERMISSION]}
          />
        </StepperStep>
        {dataMappingState.resultSchema && (
          <StepperStep title="Map columns to Results" id="map">
            <ColumnMappingTable
              inputDataFrameSpec={inputDataFrame}
              outputResultSchema={dataMappingState.resultSchema}
              mappings={dataMappingState.mappings}
              setMappings={(newMappings) => {
                setDataMappingState(updateMappings(dataMappingState, newMappings));
              }}
              errors={validationErrors}
            />
          </StepperStep>
        )}
        {BenchlingConfig.SAMPLE_TESTS_ENABLE && (
          <StepperStep title="Configure downstream target">
            <CheckboxInput
              className={css(styles.checkbox)}
              checked={targetSpecificMappingState?.target === SaveToResultsTarget.TEST_ORDER_TABLE}
              onChange={(ev) => {
                if (ev.target.checked) {
                  setTargetSpecificMappingState({
                    target: SaveToResultsTarget.TEST_ORDER_TABLE,
                    targetVersion: 0,
                    columnMappings: [],
                  });
                } else {
                  setTargetSpecificMappingState(null);
                }
              }}
              id="target-assay-checkbox"
              label="Use this result in an assay"
            />
          </StepperStep>
        )}
        {BenchlingConfig.SAMPLE_TESTS_ENABLE && targetSpecificMappingState != null && (
          <StepperStep title="Map columns for downstream target">
            <DataFrameColumnMappingTable
              templateDataFrameColumns={REQUIRED_COLUMN_DEFS_BY_TARGET.TEST_ORDER_TABLE}
              newDataFrameColumns={inputDataFrame.columns}
              setSelectedColumns={setSelectedColumnsHandler}
              selectedColumns={selectedColumns}
              errors={targetColumnValidationErrors}
            />
          </StepperStep>
        )}
      </Stepper>
    </StepConfigurationViewTemplate>
  );
}

export type SaveToResultsStepConfigurationViewProps = Pick<
  MainViewComponentProps<AnalysisMainViewItemType.SAVE_TO_RESULTS_CONFIGURATION>,
  'analysis' | 'clientStateId' | 'onCloseStepInitializationView' | 'selectedItem'
>;

export default function SaveToResultsStepConfigurationView(
  props: SaveToResultsStepConfigurationViewProps
): JSX.Element {
  // `dataMappingState` may be partially initialized in `<DataMappingModal>`.
  const [dataMappingState, setDataMappingState, resetDataMappingState] = useDataMappingState(
    props.clientStateId,
    props.selectedItem.inputDataFrameId
  );
  const [targetSpecificMappingState, setTargetSpecificMappingState] =
    useState<TargetSpecificMappingState | null>(null);

  const addSaveToResultsStep = async (
    dataMappingState: DataMappingState,
    targetSpecificMappingState: TargetSpecificMappingState | null,
    dataFrame: DataFrame
  ): Promise<void> => {
    trackAddSaveToResultsStepEvent(
      dataMappingState,
      targetSpecificMappingState,
      dataFrame,
      props.selectedItem.inputDataFrameId
    );
    logProfilerTimingEvent(SAVE_TO_RESULTS_STARTED_EVENT);
    const {id: stepId} = await addOrReplaceAndRunAnalysisStep(props.analysis.id, {
      stepType: PipelineStepType.SAVE_TO_RESULTS,
      stepVersion: 2,
      stepConfig: {
        schemaId: dataMappingState.resultSchema!.id,
        columnMapping: dataMappingState.mappings,
        targetSpecificMapping: targetSpecificMappingState,
      },
      stepInputParents: {
        [SaveToResultsStepDataName.INPUT_DATASET]: [props.selectedItem.inputDataReference],
      },
      stepInput: {
        inputDataFrameId: props.selectedItem.inputDataFrameId,
      },
    });
    props.onCloseStepInitializationView({type: AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT, stepId});
  };

  const onCancel = (): void => {
    const sourceStep = props.analysis.execution.steps.find((step) =>
      step.outputStepDatas.some((stepData) =>
        stepData.dataFrames.some((dataFrame) => dataFrame.id === props.selectedItem.inputDataFrameId)
      )
    );
    assert(sourceStep != null, 'Expected input data frame to be the output of a step.');
    props.onCloseStepInitializationView({
      type: AnalysisMainViewItemType.DATA_FRAME,
      stepId: sourceStep.id,
      dataFrameId: props.selectedItem.inputDataFrameId,
    });
    resetDataMappingState();
  };

  return (
    <BenchlingQuery<GetSubsetDataFrame, GetSubsetDataFrameVariables>
      query={GET_SUBSET_DATA_FRAME_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{dataFrameId: props.selectedItem.inputDataFrameId, limit: DEFAULT_DATA_FRAME_ROW_LIMIT}}
    >
      {({dataFrame}) => {
        assert(!isInaccessible(dataFrame), 'Expected data frame to be accessible.');
        return (
          <InternalSaveToResultsStepConfigurationView
            clientStateId={props.clientStateId}
            inputDataFrame={dataFrame.dataFrameJson}
            dataMappingState={dataMappingState}
            setDataMappingState={setDataMappingState}
            targetSpecificMappingState={targetSpecificMappingState}
            setTargetSpecificMappingState={setTargetSpecificMappingState}
            addSaveToResultsStep={() =>
              addSaveToResultsStep(dataMappingState, targetSpecificMappingState, dataFrame.dataFrameJson)
            }
            onCancel={onCancel}
          />
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  checkbox: {
    verticalAlign: 'text-top',
    marginRight: 15,
  },
});
